import { intersection } from 'lodash';
import { useCallback } from 'react';

import { useListbox } from './ListboxContext';
import ListboxRoot from './ListboxRoot';
import { ListboxDataItem, ListboxInternalProps } from './ListboxTypes';

const getNested = (item: ListboxDataItem, includeParent = false): string[] => {
  if (item.children && item.children.length) {
    const childrenValues = item.children
      .map((subitem: ListboxDataItem) => getNested(subitem))
      .flat();
    return includeParent ? [item.value, ...childrenValues] : childrenValues;
  }
  return [item.value];
};

function ListboxMultiple<T>(props: ListboxInternalProps<T>) {
  const { setSelected, parentCanBeChecked, selected } = useListbox();
  const getParent = useCallback(
    (item: ListboxDataItem) => {
      return props.items.find((parentItem: any) =>
        parentItem.options?.some((child: any) => child.id === item.value)
      );
    },
    [props.items]
  );

  const shouldAddOrRemoveParent = useCallback((parent: any, updatedSelected: string[]) => {
    if (parent && parent.options.every((child: any) => updatedSelected.includes(child.id))) {
      return true;
    }

    return false;
  }, []);

  const triggerSelect = useCallback(
    (item: ListboxDataItem) => {
      if (parentCanBeChecked) {
        const itemChildrenIds = item.childrenIds ?? [];

        if (item.isSelectAll && itemChildrenIds.length > 0) {
          setSelected(prev => {
            const itemsAreSelected = itemChildrenIds.every(childId => prev.includes(childId));
            if (itemsAreSelected && prev.length > 0) {
              return prev.filter(prevItem => !itemChildrenIds.includes(prevItem));
            } else {
              return prev.concat(itemChildrenIds).filter((val, index, self) => {
                return self.indexOf(val) === index;
              });
            }
          });
          return;
        }

        setSelected(prev => {
          if (prev.includes(item.value)) {
            return prev.filter(prevItem => prevItem !== item.value);
          }
          return [...prev, item.value];
        });
        return;
      }

      setSelected(prev => {
        let updatedSelected: string[];
        if (!item.children?.length) {
          if (prev.includes(item.value)) {
            updatedSelected = prev.filter(prevItem => prevItem !== item.value);
          } else {
            updatedSelected = [...prev, item.value];
          }
        } else {
          const nestedItems = getNested(item, props.includeParent);
          if (intersection(prev, nestedItems).length === nestedItems.length) {
            updatedSelected = prev.filter(prevItem => !nestedItems.includes(prevItem));
          } else {
            updatedSelected = [
              ...prev.filter(prevItem => !nestedItems.includes(prevItem)),
              ...nestedItems,
            ];
          }
        }

        // Verificar si se debe agregar o quitar el ID del padre
        if (props.includeParent) {
          const parent: any = getParent(item);

          if (
            parent &&
            !updatedSelected.includes(parent.id) &&
            shouldAddOrRemoveParent(parent, updatedSelected)
          ) {
            updatedSelected.push(parent.id);
          }
          if (
            parent &&
            updatedSelected.includes(parent.id) &&
            !shouldAddOrRemoveParent(parent, updatedSelected)
          ) {
            updatedSelected.splice(updatedSelected.indexOf(parent.id), 1);
          }
        }

        return updatedSelected;
      });
    },
    [setSelected, props.includeParent, getParent, shouldAddOrRemoveParent]
  );

  return (
    <ListboxRoot
      onSelectItem={triggerSelect}
      noSave={!!selected.length}
      noClean={!!selected.length}
      {...props}
    />
  );
}

export default ListboxMultiple;
